<template>
  <div class="report-container pa-4">
    <!-- Cabecera del Reporte -->
    <v-card class="mb-4 header-card elevation-2 overflow-hidden" rounded="lg">
      <div class="header-gradient px-6 py-5 d-flex align-center justify-space-between text-white">
        <div>
          <h1 class="text-h5 font-weight-bold mb-1 d-flex align-center">
            <v-icon large class="mr-2 text-white">mdi-file-document-outline</v-icon>
            Reporte de Aplicaciones Reales de Visitas
          </h1>
          <p class="text-subtitle-2 mb-0 opacity-80 font-weight-regular">
            Consulta y exportación de aplicaciones reales realizadas durante las visitas técnicas.
          </p>
        </div>
      </div>
    </v-card>

    <!-- CRUD / Listado de datos -->
    <s-crud
      title="Listado de Aplicaciones Reales"
      height="520px"
      ref="crudVisitPerformedApplicationReal"
      :filter="filter"
      :config="config"
      sortable
      searchInput
      class="elevation-1 modern-crud"
      @rowSelected="rowSelected($event)"
    >
      <!-- Slot de Filtros Personalizados -->
      <template v-slot:filter>
        <v-card class="filter-card mb-4 pa-5" outlined rounded="lg">
          <div class="d-flex align-center mb-4">
            <v-icon color="primary" class="mr-2">mdi-filter-variant</v-icon>
            <span class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Filtros de Búsqueda</span>
          </div>
          <v-row align="end" dense>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-date
                label="Fecha Desde"
                v-model="filter.BeginDate"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-date 
                label="Fecha Hasta" 
                v-model="filter.EndDate" 
                clearable 
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-campaign-area
                label="Campaña"
                v-model="filter.CmaID"
                typeArea="1"
                clearable
                noDefault
                full
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="pb-2">
              <s-select-lot
                label="Sub Lote"
                v-model="filter.FltID"
                clearable
                noDefault
                full
              />
            </v-col>
            <v-col cols="12" sm="6" md="4" class="pb-2">
              <s-select-definition
                :def="1325"
                label="Tipo de Producto"
                v-model="filter.TypeProduct"
                clearable
              />
            </v-col>
          </v-row>
        </v-card>
      </template>

      <!-- Slots para fechas formateadas -->
      <template v-slot:[`item.PapDate`]="{ item }">
        <span class="font-weight-medium grey--text text--darken-3">{{ formatDate(item.PapDate) }}</span>
      </template>
      <template v-slot:[`item.SenasaDate`]="{ item }">
        <span>{{ formatDate(item.SenasaDate) }}</span>
      </template>
      <template v-slot:[`item.SenasaCalculadoDate`]="{ item }">
        <span>{{ formatDate(item.SenasaCalculadoDate) }}</span>
      </template>
      <template v-slot:[`item.PPLDate`]="{ item }">
        <span>{{ formatDate(item.PPLDate) }}</span>
      </template>

      <!-- Slot para Cumplimiento de Senasa -->
      <template v-slot:[`item.SenasaCompliance`]="{ item }">
        <v-chip
          x-small
          v-if="item.SenasaCompliance !== null && item.SenasaCompliance !== undefined"
          :color="item.SenasaCompliance <= 0 ? 'green lighten-4' : 'red lighten-4'"
          :class="item.SenasaCompliance <= 0 ? 'green--text text--darken-3 font-weight-bold' : 'red--text text--darken-3 font-weight-bold'"
          class="custom-chip"
        >
          <v-icon x-small left :color="item.SenasaCompliance <= 0 ? 'green darken-3' : 'red darken-3'">
            {{ item.SenasaCompliance <= 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ item.SenasaCompliance <= 0 ? 'Cumple' : 'No Cumple' }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Slot para Cumplimiento Calculado de Senasa -->
      <template v-slot:[`item.SenasaComplianceCalculated`]="{ item }">
        <v-chip
          x-small
          v-if="item.SenasaComplianceCalculated !== null && item.SenasaComplianceCalculated !== undefined"
          :color="item.SenasaComplianceCalculated <= 0 ? 'green lighten-4' : 'red lighten-4'"
          :class="item.SenasaComplianceCalculated <= 0 ? 'green--text text--darken-3 font-weight-bold' : 'red--text text--darken-3 font-weight-bold'"
          class="custom-chip"
        >
          <v-icon x-small left :color="item.SenasaComplianceCalculated <= 0 ? 'green darken-3' : 'red darken-3'">
            {{ item.SenasaComplianceCalculated <= 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          {{ item.SenasaComplianceCalculated <= 0 ? 'Cumple' : 'No Cumple' }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Slot para Cultivo Aprobado -->
      <template v-slot:[`item.ApprovedCultivation`]="{ item }">
        <v-chip
          x-small
          v-if="item.ApprovedCultivation"
          :color="isApproved(item.ApprovedCultivation) ? 'teal lighten-4' : 'grey lighten-3'"
          :class="isApproved(item.ApprovedCultivation) ? 'teal--text text--darken-4 font-weight-bold' : 'grey--text text--darken-4 font-weight-bold'"
          class="custom-chip"
        >
          {{ item.ApprovedCultivation }}
        </v-chip>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Slot para la columna de acciones -->
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn
          color="indigo"
          outlined
          x-small
          class="text-none font-weight-bold px-3 py-1 custom-action-btn"
          rounded
          @click.stop="onRowAction(item)"
        >
          <v-icon left x-small>mdi-cog-outline</v-icon>
          Acciones
        </v-btn>
      </template>
    </s-crud>
  </div>
</template>

<script>
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";
import SSelectLot from "@/components/HarvestSchedule/SSelectLot.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import _sVisit from "@/services/Technicalassistance/Visit/Visit.js";

export default {
  name: "ReportVisitPerformedApplicationReal",
  components: {
    SSelectCampaignArea,
    SSelectLot,
    SSelectDefinition,
  },
  data() {
    return {
      selectedRow: null,
      
      // Filtros vinculados al s-crud
      filter: {
        BeginDate: "",
        EndDate: "",
        CmaID: null,
        FltID: null,
        TypeProduct: null,
      },

      // Configuración requerida por s-crud
      config: {
        model: {
          PpaID: "ID",
          PapDate: "date",
          SenasaDate: "date",
          SenasaCalculadoDate: "date",
          PPLDate: "date",
        },
        service: {
          pagination: (parameters, requestID) => {
            return _sVisit.TasVisitPerformedApplicationReal(parameters, requestID);
          },
        },
        headers: [
          { text: "Acciones", value: "actions", sortable: false, width: 110, align: "center" },
        //   { text: "ID Visita", value: "VstID", sortable: false, width: 90, align: "center" },
          { text: "Campaña", value: "CmaName", sortable: false, width: 140 },
          { text: "ID Lote", value: "FltID", sortable: false, width: 90, align: "center" },
          { text: "Productor", value: "Producer", sortable: false, width: 240 },
          { text: "Lote", value: "FltName", sortable: false, width: 140 },
          { text: "Tipo Cultivo", value: "TypeCultiveName", sortable: false, width: 130 },
          { text: "Variedad", value: "VrtDescription", sortable: false, width: 130 },
          { text: "Cultivo", value: "TypeCropName", sortable: false, width: 130 },
          { text: "Hectáreas Lote", value: "FltHectares", sortable: false, width: 110, align: "right" },
          { text: "Plantas Lote", value: "FltPlants", sortable: false, width: 110, align: "right" },
          { text: "Zona", value: "ZonDescription", sortable: false, width: 120 },
          { text: "Código Referencia", value: "ReferenceCode", sortable: false, width: 140 },
          { text: "Fecha Aplicación", value: "PapDate", sortable: false, width: 125, align: "center" },
          { text: "Hectáreas Aplicadas", value: "PapHectaresApplied", sortable: false, width: 140, align: "right" },
          { text: "Cant. Cilindros", value: "PapQuantityCylinder", sortable: false, width: 120, align: "right" },
          { text: "Insumo / Producto", value: "LsDescription", sortable: false, width: 200 },
          { text: "Motivo Aplicación", value: "MotivoAplicacion", sortable: false, width: 200 },
          { text: "Dosis", value: "Dose", sortable: false, width: 110, align: "right" },
          { text: "Frecuencia", value: "FrequencyAplication", sortable: false, width: 120 },
          { text: "Ingrediente Activo", value: "LsActiveIngredientName", sortable: false, width: 200 },
          { text: "Valor Referencia", value: "ReferenceValue", sortable: false, width: 130 },
          { text: "Total", value: "Total", sortable: false, width: 110, align: "right" },
          { text: "Días Senasa", value: "SenasaDay", sortable: false, width: 110, align: "center" },
          { text: "Fecha Senasa", value: "SenasaDate", sortable: false, width: 125, align: "center" },
          { text: "Cumplimiento Senasa", value: "SenasaCompliance", sortable: false, width: 150, align: "center" },
          { text: "Senasa Calculado", value: "SenasaCalculado", sortable: false, width: 130, align: "center" },
          { text: "Fecha Senasa Calc.", value: "SenasaCalculadoDate", sortable: false, width: 140, align: "center" },
          { text: "Cumplimiento Senasa Calc.", value: "SenasaComplianceCalculated", sortable: false, width: 160, align: "center" },
          { text: "Cód. PPL", value: "CodPPL", sortable: false, width: 120, align: "center" },
          { text: "Fecha PPL", value: "PPLDate", sortable: false, width: 125, align: "center" },
          { text: "Cultivo Aprobado", value: "ApprovedCultivation", sortable: false, width: 130, align: "center" },
          { text: "Tipo Acción", value: "TypeActionName", sortable: false, width: 140 },
        ],
      },
    };
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      return this.$moment(value).format("DD/MM/YYYY");
    },
    isApproved(val) {
      if (!val) return false;
      return ["SI", "S", "APROBADO", "YES", "TRUE"].includes(String(val).toUpperCase());
    },
    rowSelected(selection) {
      this.selectedRow = selection && selection.length > 0 ? selection[0] : null;
    },
    onRowAction(item) {
      this.$fun.alert(
        `Acción seleccionada para el registro con Visita ID #${item.VstID} y Lote ${item.FltName || ''}. La funcionalidad se detallará más adelante.`,
        "info"
      );
    },
  },
};
</script>

<style scoped>
.report-container {
  background-color: #f7f9fc;
  min-height: 100vh;
}

/* Cabecera gradiente elegante */
.header-card {
  border: none;
}
.header-gradient {
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
  color: white;
}
.opacity-80 {
  opacity: 0.8;
}

/* Tarjeta de filtros moderna */
.filter-card {
  background-color: white;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

/* Ajustes premium en chips */
.custom-chip {
  font-size: 0.75rem !important;
  height: 24px;
  border-radius: 4px;
}

/* Botón de acciones personalizado */
.custom-action-btn {
  border-width: 1.5px;
  transition: all 0.2s ease-in-out;
}
.custom-action-btn:hover {
  background-color: #3f51b5 !important;
  color: white !important;
  box-shadow: 0 2px 5px rgba(63, 81, 181, 0.4);
}

/* Customización del CRUD */
.modern-crud >>> .v-data-table {
  background: white;
  border-radius: 8px;
}
.modern-crud >>> th {
  background-color: #f5f5f5 !important;
  font-weight: 700 !important;
  color: #333333 !important;
}
</style>
